import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { api } from "../api/httpClient";
import SEO from "../components/SEO";
import VendorView from "./VendorView";
import LocationView from "./LocationView";
import CityView from "./CityView";
import CountryView from "./CountryView";
import { VendorLocationView } from "./VendorLocationView";

const LandingPageContainer = () => {
  const { vendorSlug, countrySlug, countryCode, citySlug, locationSlug } = useParams();
  const location = useLocation();

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
// Inside LandingPageContainer.js -> useEffect
const fetchLandingPage = async () => {
  setLoading(true);
  setError(false);

  try {
    let type = "vendor";
    // City pages are keyed by the CMS's own literal path (e.g.
    // "/rent-a-car/canada-toronto"), not a countryCode/citySlug pair —
    // see landingUrl below.
    if (location.pathname.startsWith("/rent-a-car/")) type = "city";
    if (location.pathname.includes("/countries/")) type = "country";
    if (location.pathname.includes("/locations/")) type = "location";
    if (vendorSlug && locationSlug) type = "vendorLocation";

    const payload = {
      pageType: type,
      // Sent verbatim so website-api can look up the exact Redis key the
      // CMS wrote the page under — same pattern LandingUrlHandler.js uses
      // for the /landing-link/search flow.
      landingUrl: location.pathname,
      vendorSlug,
      countrySlug,
      countryCode,
      citySlug,
      locationSlug,
      locale: "en-US",
    };

    const response = await api.getLandingPage(payload);

    // --- NEW LOGIC TO UNWRAP DATA ---
    let actualData = response;
    
    // Check if n8n returned an array [ {...} ]
    if (Array.isArray(response) && response.length > 0) {
      actualData = response[0];
    }
    
    // If n8n wrapped it in an extra body object { body: {...} }
    if (actualData && actualData.body) {
      actualData = actualData.body;
    }

    if (actualData) {
      setPageData(actualData);
    } else {
      setError(true);
    }
    // ---------------------------------

  } catch (err) {
    console.error("Failed to load landing page:", err);
    setError(true);
  } finally {
    setLoading(false);
  }
};

    fetchLandingPage();
  }, [vendorSlug, citySlug, locationSlug, countryCode, location.pathname]);

  // High-fidelity Loading State
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-slate-50">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="h-12 w-12 border-4 border-t-gray border-t-primary rounded-full animate-spin"></div>
          <p className="text-slate-500 font-bold tracking-tight">Finding the best deals for you...</p>
        </div>
      </div>
    );
  }

  // High-fidelity Error State
  if (error || !pageData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4 bg-slate-50">
        <div className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 max-w-md">
          <h2 className="text-2xl font-black text-navy mb-4">Something went wrong</h2>
          <p className="text-slate-500 mb-8 leading-relaxed">
            We couldn't load the requested car rental page. It might be moved or currently unavailable.
          </p>
          <a href="/" className="inline-block px-8 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-colors shadow-lg">
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={pageData.seo?.title || "Car Rental Aggregator"}
        description={pageData.seo?.metaDescription}
        canonical={pageData.seo?.canonicalUrl}
        structuredData={pageData.seo?.structuredData}
      />

      <main className="min-h-screen bg-slate-50 pb-20 font-sans">
        {pageData.pageType === "vendor" && <VendorView data={pageData} />}
        
        {/* Placeholder logic for other views - passing the shared API data structure */}
        {pageData.pageType === "city" && <CityView data={pageData} />}
        {pageData.pageType === "location" && <LocationView data={pageData} />}
        {pageData.pageType === "country" && <CountryView data={pageData} />}
        {pageData.pageType === "vendorLocation" && (
          <VendorLocationView data={pageData} isVendorSpecific={true} />
        )}
      </main>
    </>
  );
};

export default LandingPageContainer;